import React, { useState, useEffect } from 'react';
import {
  View, Text, FlatList, TextInput, Modal, StyleSheet,
  ActivityIndicator, Alert, ScrollView, Switch, TouchableOpacity,
  useWindowDimensions, Image,
} from 'react-native';
import {
  fetchBranches, registerBranch, updateBranch, deleteBranch,
  uploadBranchImage, uploadBranchBanner, deleteStorageUrl, BranchModel, Store,
} from '../query/branchService';

const C = {
  primary: '#21808D', success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', bg: '#f6f8fb', card: '#ffffff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

const STEPS = [
  { title: 'Account Setup',    sub: 'Login credentials for this branch',   icon: '🔑' },
  { title: 'Basic Info',       sub: 'Store name and contact details',       icon: '🏢' },
  { title: 'Charges',          sub: 'Delivery fees and order limits',       icon: '💰' },
  { title: 'Location',         sub: 'Geographic coordinates',               icon: '📍' },
  { title: 'Content & Status', sub: 'Announcements and operating status',   icon: '📢' },
  { title: 'Media & Stores',   sub: 'Logo, banners and linked stores',      icon: '🖼️' },
];

const emptyStore = (): Store => ({ name: '', image: '' });

// Defined outside to prevent TextInput focus loss on re-render
const F = ({ label, value, onChange, placeholder, keyType, multiline, readonly }: any) => (
  <View style={wi.fg}>
    <Text style={wi.label}>{label}</Text>
    <TextInput
      style={[wi.input, multiline && { height: 80 }, readonly && { backgroundColor: '#f1f5f9', color: C.textMuted }]}
      value={value} onChangeText={readonly ? undefined : onChange}
      placeholder={placeholder || label} keyboardType={keyType || 'default'}
      multiline={multiline} placeholderTextColor={C.textMuted} editable={!readonly}
    />
  </View>
);

export default function BranchManagementScreen() {
  const { width } = useWindowDimensions();
  const isMobile = width < 600;

  const [branches, setBranches] = useState<BranchModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [step, setStep] = useState(0);

  // Fields
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [storename, setStorename] = useState('');
  const [phone, setPhone] = useState('');
  const [pincode, setPincode] = useState('');
  const [branchId, setBranchId] = useState('');
  const [storecate, setStorecate] = useState('');
  const [storeuid, setStoreuid] = useState('');
  const [delivery, setDelivery] = useState('0');
  const [kmcharge, setKmcharge] = useState('0');
  const [minAmount, setMinAmount] = useState('0');
  const [packing, setPacking] = useState('0');
  const [service, setService] = useState('0');
  const [tax, setTax] = useState('0');
  const [range, setRange] = useState('0');
  const [radius, setRadius] = useState('');
  const [storeLat, setStoreLat] = useState('');
  const [storeLon, setStoreLon] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const [policy, setPolicy] = useState('');
  const [online, setOnline] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerImages, setBannerImages] = useState<string[]>([]);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => { loadBranches(); }, []);

  const loadBranches = async () => {
    setLoading(true);
    try { setBranches(await fetchBranches()); }
    catch { Alert.alert('Error', 'Failed to load branches'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setCurrentId(null); setEmail(''); setPassword(''); setStorename('');
    setPhone(''); setPincode(''); setBranchId(''); setStorecate(''); setStoreuid('');
    setDelivery('0'); setKmcharge('0'); setMinAmount('0'); setPacking('0');
    setService('0'); setTax('0'); setRange('0'); setRadius('');
    setStoreLat(''); setStoreLon('');
    setAnnouncement(''); setPolicy(''); setOnline(true);
    setLogoUrl(''); setBannerImages([]); setStores([]);
    setStep(0);
  };

  const openCreate = () => { resetForm(); setIsEditing(false); setModalVisible(true); };

  const openEdit = (b: BranchModel) => {
    setCurrentId(b.uid); setEmail(b.email || ''); setPassword('');
    setStorename(b.storename); setPhone(b.phone); setPincode(b.pincode);
    setBranchId(b.branchId); setStorecate(b.storecate); setStoreuid(b.storeuid);
    setDelivery(String(b.delivery)); setKmcharge(String(b.kmcharge));
    setMinAmount(String(b.minAmount)); setPacking(String(b.packing));
    setService(String(b.service)); setTax(String(b.tax));
    setRange(String(b.range)); setRadius(b.radius);
    setStoreLat(b.storeLat); setStoreLon(b.storeLon);
    setAnnouncement(b.announcement); setPolicy(b.policy); setOnline(b.online);
    setLogoUrl(b.image || '');
    setBannerImages(Array.isArray(b.bannerImages) ? b.bannerImages : []);
    setStores(Array.isArray(b.store) ? b.store : []);
    setStep(0); setIsEditing(true); setModalVisible(true);
  };

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!isEditing && !email.trim()) { Alert.alert('Required', 'Email is required'); return false; }
      if (!isEditing && password.length < 6) { Alert.alert('Required', 'Password must be at least 6 characters'); return false; }
    }
    if (step === 1) {
      if (!storename.trim()) { Alert.alert('Required', 'Store name is required'); return false; }
      if (!phone.trim()) { Alert.alert('Required', 'Phone is required'); return false; }
    }
    return true;
  };

  const goNext = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const handleLogo = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file: File = e.target.files[0];
      if (!file) return;
      setLogoUploading(true);
      try {
        const uid = currentId || `tmp_${Date.now()}`;
        setLogoUrl(await uploadBranchImage(file, uid));
      } catch { Alert.alert('Error', 'Logo upload failed'); }
      finally { setLogoUploading(false); }
    };
    input.click();
  };

  const handleBannerAdd = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
    input.onchange = async (e: any) => {
      const files: File[] = Array.from(e.target.files);
      if (!files.length) return;
      setBannerUploading(true);
      try {
        const uid = currentId || `tmp_${Date.now()}`;
        const urls = await Promise.all(files.map((f, i) => uploadBranchBanner(f, uid, bannerImages.length + i)));
        setBannerImages(prev => [...prev, ...urls]);
      } catch { Alert.alert('Error', 'Banner upload failed'); }
      finally { setBannerUploading(false); }
    };
    input.click();
  };

  const removeBanner = async (idx: number) => {
    await deleteStorageUrl(bannerImages[idx]);
    setBannerImages(prev => prev.filter((_, i) => i !== idx));
  };

  const addStore = () => setStores(prev => [...prev, emptyStore()]);
  const removeStore = (idx: number) => setStores(prev => prev.filter((_, i) => i !== idx));
  const updateStoreField = (idx: number, k: keyof Store, v: string) =>
    setStores(prev => prev.map((s, i) => i === idx ? { ...s, [k]: v } : s));

  const handleSave = async () => {
    if (!validateStep()) return;
    setSaving(true);
    try {
      const data = {
        announcement, bannerImages, branchId,
        delivery: parseInt(delivery) || 0,
        image: logoUrl,
        kmcharge: parseInt(kmcharge) || 0,
        minAmount: parseInt(minAmount) || 0,
        online, packing: parseInt(packing) || 0,
        phone, pincode, policy, radius,
        range: parseInt(range) || 0,
        service: parseInt(service) || 0,
        store: stores, storeLat, storeLon, storecate, storename, storeuid,
        tax: parseInt(tax) || 0,
      };
      if (isEditing && currentId) {
        await updateBranch(currentId, data);
      } else {
        await registerBranch(email.trim(), password, data);
      }
      await loadBranches();
      setModalVisible(false);
      Alert.alert('Success', `Branch ${isEditing ? 'updated' : 'registered'}!`);
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to save branch');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: BranchModel) => {
    Alert.alert('Delete Branch', `Delete "${item.storename}"? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try { await deleteBranch(item.uid, item.image, item.bannerImages); await loadBranches(); }
        catch (e: any) { Alert.alert('Error', e.message); }
      }},
    ]);
  };


  // ── Step content ──────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <>
            {isEditing ? (
              <F label="Email (read-only)" value={email} readonly />
            ) : (
              <>
                <F label="Email *" value={email} onChange={setEmail} keyType="email-address" />
                <F label="Password * (min 6 chars)" value={password} onChange={setPassword} />
              </>
            )}
          </>
        );
      case 1:
        return (
          <>
            <F label="Store Name *" value={storename} onChange={setStorename} />
            <F label="Phone *" value={phone} onChange={setPhone} keyType="phone-pad" />
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Pincode" value={pincode} onChange={setPincode} /></View>
              <View style={{ flex: 1 }}><F label="Branch ID" value={branchId} onChange={setBranchId} /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Category" value={storecate} onChange={setStorecate} /></View>
              <View style={{ flex: 1 }}><F label="Store UID" value={storeuid} onChange={setStoreuid} /></View>
            </View>
          </>
        );
      case 2:
        return (
          <>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Delivery (₹)" value={delivery} onChange={setDelivery} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Packing (₹)" value={packing} onChange={setPacking} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Service (₹)" value={service} onChange={setService} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="KM Charge (₹)" value={kmcharge} onChange={setKmcharge} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Min Order (₹)" value={minAmount} onChange={setMinAmount} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Tax (%)" value={tax} onChange={setTax} keyType="numeric" /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Range (km)" value={range} onChange={setRange} keyType="numeric" /></View>
              <View style={{ flex: 1 }}><F label="Radius" value={radius} onChange={setRadius} /></View>
            </View>
          </>
        );
      case 3:
        return (
          <>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Latitude" value={storeLat} onChange={setStoreLat} keyType="decimal-pad" /></View>
              <View style={{ flex: 1 }}><F label="Longitude" value={storeLon} onChange={setStoreLon} keyType="decimal-pad" /></View>
            </View>
          </>
        );
      case 4:
        return (
          <>
            <F label="Announcement" value={announcement} onChange={setAnnouncement} multiline />
            <F label="Policy" value={policy} onChange={setPolicy} multiline />
            <View style={wi.switchRow}>
              <View>
                <Text style={wi.switchLabel}>Online Status</Text>
                <Text style={wi.switchSub}>Branch is accepting orders</Text>
              </View>
              <Switch value={online} onValueChange={setOnline} trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
          </>
        );
      case 5:
        return (
          <>
            {/* Logo */}
            <Text style={wi.sectionLabel}>Branch Logo</Text>
            <View style={wi.logoSection}>
              <TouchableOpacity style={wi.logoWrap} onPress={handleLogo} disabled={logoUploading}>
                {logoUploading ? <ActivityIndicator color={C.primary} /> :
                  logoUrl ? <Image source={{ uri: logoUrl }} style={wi.logoImg} /> :
                  <View style={wi.logoPlaceholder}>
                    <Text style={{ fontSize: 28 }}>🏢</Text>
                    <Text style={wi.logoHint}>Tap to upload</Text>
                  </View>
                }
              </TouchableOpacity>
              {logoUrl ? (
                <TouchableOpacity onPress={async () => { await deleteStorageUrl(logoUrl); setLogoUrl(''); }}>
                  <Text style={wi.removeText}>Remove</Text>
                </TouchableOpacity>
              ) : null}
            </View>

            {/* Banners */}
            <Text style={wi.sectionLabel}>Banner Images</Text>
            {bannerImages.map((url, idx) => (
              <View key={idx} style={wi.bannerRow}>
                {url ? <Image source={{ uri: url }} style={wi.bannerThumb} /> : null}
                <Text style={wi.bannerUrl} numberOfLines={1}>{url}</Text>
                <TouchableOpacity onPress={() => removeBanner(idx)} style={wi.bannerDel}>
                  <Text style={wi.bannerDelTxt}>✕</Text>
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={[wi.dashedBtn, bannerUploading && { opacity: 0.6 }]} onPress={handleBannerAdd} disabled={bannerUploading}>
              {bannerUploading ? <ActivityIndicator color={C.primary} size="small" /> :
                <Text style={wi.dashedBtnTxt}>+ Upload Banners</Text>}
            </TouchableOpacity>

            {/* Linked Stores */}
            <Text style={[wi.sectionLabel, { marginTop: 16 }]}>Linked Stores</Text>
            {stores.map((s, idx) => (
              <View key={idx} style={wi.storeEntry}>
                <View style={{ flex: 1 }}>
                  <F label="Store Name" value={s.name} onChange={(v: string) => updateStoreField(idx, 'name', v)} />
                  <F label="Image URL" value={s.image} onChange={(v: string) => updateStoreField(idx, 'image', v)} />
                </View>
                <TouchableOpacity onPress={() => removeStore(idx)} style={wi.storeRemove}>
                  <Text style={wi.storeRemoveTxt}>✕</Text>
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={wi.dashedBtn} onPress={addStore}>
              <Text style={wi.dashedBtnTxt}>+ Add Store</Text>
            </TouchableOpacity>
          </>
        );
      default: return null;
    }
  };

  // ── Card ──────────────────────────────────────────────────────────
  const renderCard = ({ item }: { item: BranchModel }) => (
    <View style={cs.card}>
      <View style={cs.cardTop}>
        {item.image
          ? <Image source={{ uri: item.image }} style={cs.logo} />
          : <View style={cs.logoPlaceholder}><Text style={cs.logoInit}>{(item.storename || 'B')[0].toUpperCase()}</Text></View>
        }
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <View style={[cs.dot, { backgroundColor: item.online ? C.success : '#94a3b8' }]} />
            <Text style={cs.name}>{item.storename || 'Branch'}</Text>
          </View>
          <Text style={cs.email}>{item.email || item.branchId || '—'}</Text>
          <Text style={cs.phone}>{item.phone || '—'}</Text>
        </View>
        <View style={[cs.badge, { backgroundColor: item.online ? C.successLight : '#f1f5f9' }]}>
          <Text style={[cs.badgeTxt, { color: item.online ? C.success : C.textMuted }]}>
            {item.online ? 'Online' : 'Offline'}
          </Text>
        </View>
      </View>
      {item.announcement ? (
        <View style={cs.annoBox}><Text style={cs.annoTxt} numberOfLines={2}>📢 {item.announcement}</Text></View>
      ) : null}
      {item.bannerImages?.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 10 }}>
          {item.bannerImages.map((url, i) => <Image key={i} source={{ uri: url }} style={cs.bannerThumb} />)}
        </ScrollView>
      )}
      <View style={cs.stats}>
        {[['Delivery', `₹${item.delivery}`], ['Packing', `₹${item.packing}`],
          ['Service', `₹${item.service}`], ['Tax', `${item.tax}%`],
          ['Min Amt', `₹${item.minAmount}`], ['KM Rate', `₹${item.kmcharge}`]].map(([l, v]) => (
          <View key={l} style={cs.stat}>
            <Text style={cs.statL}>{l}</Text>
            <Text style={cs.statV}>{v}</Text>
          </View>
        ))}
      </View>
      {item.store?.length > 0 && (
        <View style={cs.storeRow}>
          {item.store.map((s, i) => (
            <View key={i} style={cs.storeChip}><Text style={cs.storeChipTxt}>{s.name}</Text></View>
          ))}
        </View>
      )}
      <View style={cs.actions}>
        <TouchableOpacity style={cs.editBtn} onPress={() => openEdit(item)}><Text style={cs.editTxt}>Edit</Text></TouchableOpacity>
        <TouchableOpacity style={cs.delBtn} onPress={() => handleDelete(item)}><Text style={cs.delTxt}>Delete</Text></TouchableOpacity>
      </View>
    </View>
  );

  if (loading) return (
    <View style={cs.loader}>
      <ActivityIndicator size="large" color={C.primary} />
      <Text style={{ marginTop: 12, color: C.textMuted }}>Loading branches...</Text>
    </View>
  );

  return (
    <View style={cs.container}>
      <View style={[cs.header, isMobile && cs.headerM]}>
        <View>
          <Text style={cs.title}>Branches</Text>
          <Text style={cs.sub}>{branches.length} branch{branches.length !== 1 ? 'es' : ''}</Text>
        </View>
        <TouchableOpacity style={cs.addBtn} onPress={openCreate}>
          <Text style={cs.addBtnTxt}>+ Register Branch</Text>
        </TouchableOpacity>
      </View>

      <FlatList
        data={branches} keyExtractor={item => item.uid} renderItem={renderCard}
        numColumns={isMobile ? 1 : 2} key={isMobile ? 'one' : 'two'}
        columnWrapperStyle={!isMobile ? { gap: 14 } : undefined}
        contentContainerStyle={{ paddingBottom: 50 }}
        ListEmptyComponent={
          <View style={cs.empty}>
            <Text style={{ fontSize: 48 }}>🏢</Text>
            <Text style={cs.emptyTitle}>No branches yet</Text>
          </View>
        }
      />

      {/* ── Step Wizard Modal ── */}
      <Modal visible={modalVisible} animationType="slide" transparent={false}>
        <View style={wi.screen}>
          {/* Top bar */}
          <View style={wi.topBar}>
            <TouchableOpacity style={wi.closeBtn} onPress={() => setModalVisible(false)}>
              <Text style={wi.closeTxt}>✕</Text>
            </TouchableOpacity>
            <Text style={wi.topTitle}>{isEditing ? 'Edit Branch' : 'Register Branch'}</Text>
            <View style={{ width: 36 }} />
          </View>

          {/* Progress */}
          <View style={wi.progressArea}>
            <View style={wi.progressBarOuter}>
              <View style={[wi.progressBarInner, { width: `${((step + 1) / STEPS.length) * 100}%` as any }]} />
            </View>
            <View style={wi.progressRow}>
              <Text style={wi.progressLabel}>{STEPS[step].icon}  {STEPS[step].title}</Text>
              <Text style={wi.progressCount}>{step + 1} / {STEPS.length}</Text>
            </View>
            <Text style={wi.progressSub}>{STEPS[step].sub}</Text>
            {/* Step dots */}
            <View style={wi.dots}>
              {STEPS.map((_, i) => (
                <TouchableOpacity key={i} onPress={() => setStep(i)} style={[wi.dot, i === step && wi.dotActive, i < step && wi.dotDone]}>
                  {i < step
                    ? <Text style={wi.dotTxtDone}>✓</Text>
                    : <Text style={[wi.dotTxt, i === step && { color: '#fff' }]}>{i + 1}</Text>
                  }
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Content */}
          <ScrollView style={{ flex: 1 }} contentContainerStyle={wi.scrollContent} showsVerticalScrollIndicator={false}>
            {renderStepContent()}
          </ScrollView>

          {/* Footer */}
          <View style={wi.footer}>
            {step > 0
              ? <TouchableOpacity style={wi.backBtn} onPress={goBack}>
                  <Text style={wi.backBtnTxt}>← Back</Text>
                </TouchableOpacity>
              : <View style={{ flex: 1 }} />
            }
            {step < STEPS.length - 1 ? (
              <TouchableOpacity style={wi.nextBtn} onPress={goNext}>
                <Text style={wi.nextBtnTxt}>Next →</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={[wi.saveBtn, saving && { opacity: 0.6 }]} onPress={handleSave} disabled={saving}>
                {saving ? <ActivityIndicator color="#fff" size="small" /> :
                  <Text style={wi.saveBtnTxt}>{isEditing ? 'Update Branch' : 'Register Branch'}</Text>}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ── Card styles ───────────────────────────────────────────────────
const cs = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f6f8fb', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  headerM: { flexDirection: 'column', alignItems: 'flex-start', gap: 12 },
  title: { fontSize: 24, fontWeight: '700', color: '#1a2332' },
  sub: { fontSize: 13, color: '#64748b', marginTop: 2 },
  addBtn: { backgroundColor: '#21808D', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  addBtnTxt: { color: '#fff', fontWeight: '600', fontSize: 14 },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  card: {
    flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 14,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 8, elevation: 3,
    borderTopWidth: 3, borderTopColor: '#21808D',
  },
  cardTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 10 },
  logo: { width: 50, height: 50, borderRadius: 12, backgroundColor: '#e8f5f7' },
  logoPlaceholder: { width: 50, height: 50, borderRadius: 12, backgroundColor: '#e8f5f7', justifyContent: 'center', alignItems: 'center' },
  logoInit: { fontSize: 22, fontWeight: '700', color: '#21808D' },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 4 },
  name: { fontSize: 15, fontWeight: '700', color: '#1a2332' },
  email: { fontSize: 12, color: '#21808D', marginTop: 2 },
  phone: { fontSize: 12, color: '#64748b', marginTop: 1 },
  badge: { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  badgeTxt: { fontSize: 11, fontWeight: '600' },

  annoBox: { backgroundColor: '#fef9c3', borderRadius: 8, padding: 8, marginBottom: 10 },
  annoTxt: { fontSize: 12, color: '#713f12' },
  bannerThumb: { width: 80, height: 48, borderRadius: 8, marginRight: 8, backgroundColor: '#e2e8f0' },

  stats: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  stat: { backgroundColor: '#f6f8fb', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, minWidth: 72 },
  statL: { fontSize: 10, color: '#64748b', marginBottom: 2 },
  statV: { fontSize: 13, fontWeight: '600', color: '#1a2332' },

  storeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  storeChip: { backgroundColor: '#e8f5f7', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 4 },
  storeChipTxt: { fontSize: 12, color: '#21808D', fontWeight: '500' },

  actions: { flexDirection: 'row', gap: 10 },
  editBtn: { flex: 1, backgroundColor: '#e8f5f7', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  editTxt: { color: '#21808D', fontWeight: '600', fontSize: 13 },
  delBtn: { flex: 1, backgroundColor: '#fee2e2', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  delTxt: { color: '#ef4444', fontWeight: '600', fontSize: 13 },

  empty: { alignItems: 'center', paddingTop: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#1a2332', marginTop: 12 },
});

// ── Wizard styles ─────────────────────────────────────────────────
const wi = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#fff' },
  topBar: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16,
    paddingTop: 16, paddingBottom: 14, backgroundColor: '#fff',
    borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12,
  },
  closeBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: '#64748b', fontWeight: '600' },
  topTitle: { flex: 1, fontSize: 18, fontWeight: '700', color: '#1a2332', textAlign: 'center' },

  progressArea: { backgroundColor: '#f8fafc', paddingHorizontal: 20, paddingTop: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  progressBarOuter: { height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, marginBottom: 10 },
  progressBarInner: { height: 4, backgroundColor: '#21808D', borderRadius: 2 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  progressLabel: { fontSize: 15, fontWeight: '700', color: '#1a2332' },
  progressCount: { fontSize: 12, color: '#64748b', fontWeight: '600' },
  progressSub: { fontSize: 12, color: '#64748b', marginBottom: 10 },
  dots: { flexDirection: 'row', gap: 6 },
  dot: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#e2e8f0', justifyContent: 'center', alignItems: 'center' },
  dotActive: { backgroundColor: '#21808D' },
  dotDone: { backgroundColor: '#10b981' },
  dotTxt: { fontSize: 11, fontWeight: '700', color: '#94a3b8' },
  dotTxtDone: { fontSize: 11, fontWeight: '700', color: '#fff' },

  scrollContent: { padding: 20, paddingBottom: 24 },

  footer: { flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#e2e8f0', backgroundColor: '#fff' },
  backBtn: { flex: 1, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  backBtnTxt: { color: '#64748b', fontWeight: '600', fontSize: 15 },
  nextBtn: { flex: 2, backgroundColor: '#21808D', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  nextBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
  saveBtn: { flex: 2, backgroundColor: '#10b981', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  saveBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },

  fg: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#1a2332', backgroundColor: '#fafafa' },
  row: { flexDirection: 'row', gap: 12 },

  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 12, paddingHorizontal: 14, backgroundColor: '#fafafa', marginBottom: 4 },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#1a2332' },
  switchSub: { fontSize: 12, color: '#64748b', marginTop: 2 },

  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#21808D', letterSpacing: 0.5, marginBottom: 10, textTransform: 'uppercase' as const },

  logoSection: { alignItems: 'center', marginBottom: 16 },
  logoWrap: { width: 100, height: 100, borderRadius: 16, backgroundColor: '#e8f5f7', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#21808D', borderStyle: 'dashed' as const, overflow: 'hidden' },
  logoImg: { width: 100, height: 100, borderRadius: 14 },
  logoPlaceholder: { alignItems: 'center', gap: 4 },
  logoHint: { fontSize: 11, color: '#21808D', fontWeight: '600' },
  removeText: { fontSize: 12, color: '#ef4444', fontWeight: '600', marginTop: 6 },

  bannerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8, backgroundColor: '#f6f8fb', borderRadius: 10, padding: 8 },
  bannerThumb: { width: 56, height: 36, borderRadius: 6, backgroundColor: '#e2e8f0' },
  bannerUrl: { flex: 1, fontSize: 12, color: '#64748b' },
  bannerDel: { width: 26, height: 26, borderRadius: 13, backgroundColor: '#fee2e2', justifyContent: 'center', alignItems: 'center' },
  bannerDelTxt: { color: '#ef4444', fontSize: 12, fontWeight: '700' },

  dashedBtn: { borderWidth: 1, borderColor: '#21808D', borderStyle: 'dashed' as const, borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginBottom: 8 },
  dashedBtnTxt: { color: '#21808D', fontWeight: '600', fontSize: 13 },

  storeEntry: { flexDirection: 'row', gap: 10, backgroundColor: '#f6f8fb', borderRadius: 12, padding: 12, marginBottom: 10, alignItems: 'flex-start' },
  storeRemove: { width: 30, height: 30, borderRadius: 15, backgroundColor: '#fee2e2', justifyContent: 'center', alignItems: 'center', marginTop: 32 },
  storeRemoveTxt: { color: '#ef4444', fontWeight: '700' },
});
